/**
 * Local ASR capability warmup: measures once per session whether this device can
 * decode fast enough to serve reading locally, and translates the verdict into the
 * session's placement routing decision.
 *
 * Extracted from SpeechStream (TT-1231). The layering is one-way: this module
 * MEASURES, CursorOrchestrator STORES the resulting routing state, and consumers
 * (SpeechStream's per-phrase gate, the ASR-connection waits, placement_manager
 * labeling, story graph downloads) ASK the orchestrator. Neither the orchestrator
 * nor anything else imports this module's state back.
 *
 * The runtime lifecycle itself — creating, disposing and generation-counting the
 * handler — deliberately stays in SpeechStream. This module never reaches for it;
 * it receives a `getRuntime` accessor and reads through that. See warmLocalAsrForStory.
 */

import { WARMUP_VERDICT } from '../LocalPlacementAsrHandler';
import {
  WARMUP_CAP_MS,
  WARMUP_BUDGET_RESCUE_MIN_STEADY_SAMPLES,
  WARMUP_BUDGET_RESCUE_P75_BAR_FACTOR,
  WARMUP_BUDGET_RESCUE_MAX_OVER_BAR_RATIO,
} from './localAsrWarmupTuning';
import {
  LOCAL_ASR_ROUTE,
  setLocalAsrWarmupRouteDecision,
  getLocalAsrWarmupRouteDecision,
  setLocalAsrWarmupDeadlineOverrun,
  clearLocalAsrWarmupDeadlineOverrun,
  resetLocalAsrWarmupRouting,
} from '../CursorOrchestrator';
import { getStoryPhraseCount, getStoryPhrases } from './vosk/voskBiasedGraph';
import {
  recordLocalAsrLifecycle,
  recordLocalAsrFailure,
  LOCAL_ASR_FALLBACK_REASON,
} from './localAsrLifecycle';

// Warmup is once per SESSION, not once per story. The runtime deliberately survives
// activity transitions (resetLocalAsrForNextActivity keeps the worker alive rather
// than re-pay cold start), so by story 2 the JIT is tiered and the capability
// question is already answered — re-measuring would add loader time for a verdict
// that cannot have changed. Cleared only by resetLocalAsrWarmup, which
// disposeLocalPlacementAsr calls as it tears the runtime down, so the latch and the
// thing it describes go together.
let localAsrWarmupStarted = false;
// How long story launch will wait on the capability measurement. Deliberately above
// WARMUP_CAP_MS so the cap is what normally ends the run and this only fires when
// something is stuck — see warmLocalAsrForStory.
const LOCAL_ASR_WARMUP_DEADLINE_MS = WARMUP_CAP_MS + 1500;

/**
 * Whether this session has already spent its once-per-session warmup latch, i.e. whether a
 * further warmLocalAsrForStory call can still measure anything.
 *
 * Exists so a caller that reports on its own warm point can tell "this hold measured the
 * device" apart from "this hold found the answer already sitting there". Those are
 * indistinguishable from the route decision alone, because the decision is module state
 * that outlives any one story (see CursorOrchestrator) — so a story that measured nothing
 * still reads back a decision an earlier story latched. The pre-checks ASR check needs the
 * distinction because the mid-session mic troubleshoot rewinds preChecksDone and runs it
 * again mid-session, long after the real story spent the latch (TT-1217).
 * @returns {boolean}
 */
export function isLocalAsrWarmupSpent() {
  return localAsrWarmupStarted;
}

/**
 * Whether a BUDGET_EXHAUSTED run's steady-state distribution clears the rescue
 * thresholds (TT-1300). Thresholds live in localAsrWarmupTuning.js.
 *
 * Reads the fields the run already logged: enough steady samples to trust, p75 under
 * a fraction of the bar (`alreadyFastMs`, the exact bar the verdict was reached
 * against), and a thin tail (`samplesOverBar / steadySamples`). Does not check the
 * verdict — the caller does.
 *
 * Conservative: a straddler whose p75 sits at/over the bar, a heavy-tailed device
 * whose over-bar fraction exceeds the ratio, and a thin-sample run all return false.
 * @param {object} summary The settled warmup summary.
 * @param {number} [summary.steadySamples] Steady-state sample count, cold sample excluded.
 * @param {number} [summary.p75InferenceMs] 75th-percentile steady inference time (ms).
 * @param {number} [summary.alreadyFastMs] The bar the verdict was judged against (ms).
 * @param {number} [summary.samplesOverBar] Steady samples strictly over the bar.
 * @returns {boolean}
 */
function budgetExhaustedRoutesLocal(summary) {
  const {
    steadySamples, p75InferenceMs, alreadyFastMs, samplesOverBar,
  } = summary;
  return (
    Number.isFinite(steadySamples)
    && steadySamples >= WARMUP_BUDGET_RESCUE_MIN_STEADY_SAMPLES
    && Number.isFinite(alreadyFastMs)
    && Number.isFinite(p75InferenceMs)
    && p75InferenceMs <= alreadyFastMs * WARMUP_BUDGET_RESCUE_P75_BAR_FACTOR
    && Number.isFinite(samplesOverBar)
    && samplesOverBar / steadySamples <= WARMUP_BUDGET_RESCUE_MAX_OVER_BAR_RATIO
  );
}

/**
 * Consumes a settled warmup run and latches the session's placement routing decision
 * (TT-1169). Routing meaning of every outcome, decided here and only here:
 *
 *   FAST             → local. Stride decode beat the bar across the trend window.
 *   SLOW             → server. Plateaued above the bar, corroborated.
 *   BUDGET_EXHAUSTED → server by default, but rescued to local when the steady-state
 *                      distribution already collected shows a jittery-but-fast device
 *                      (TT-1300). A FAST verdict needs a unanimous, consecutive window;
 *                      a device fast on median but jittery keeps straddling the bar (a
 *                      single spike poisons the window), never latches, burns the cap,
 *                      and lands here despite a p75 comfortably under budget. On this
 *                      verdict budgetExhaustedRoutesLocal consults the already-logged
 *                      distribution (see localAsrWarmupTuning): enough samples, p75 under
 *                      a fraction of the bar, and a thin over-bar tail → local; a
 *                      straddler, a heavy tail, a thin-sample run, or a deadline overrun
 *                      still → server. Admits on p75-and-thin-tail rather than FAST's
 *                      unanimous window, without changing the bar itself.
 *   ERROR            → server. The runtime is unhealthy; more decode is not the cure.
 *   CANCELLED        → depends on who cancelled, which only this layer knows. The LAUNCH
 *                      DEADLINE means the device could not finish one iteration inside
 *                      cap + 1.5s — an implicit measurement far above any bar, so server.
 *                      LIVE AUDIO is not a capability answer: nothing is latched and the
 *                      once-per-session latch is RELEASED so the next story re-measures.
 *                      Retry only the run whose device was never measured.
 *   no verdict       → splits on the deadline like CANCELLED. If it fired, that wait IS
 *                      the measurement (server) — a run too broken to summarise must not
 *                      earn a more permissive answer. Otherwise no decision, latch spent.
 *
 * A latched decision is sticky for the session and cleared only with the runtime
 * lifecycle, never at activity boundaries. Undecided sessions route on eligibility.
 *
 * Pushed to CursorOrchestrator, which owns the server-vs-local answer, so one write
 * routes every consumer: the per-phrase gate, the ASR-connection waits, the
 * placement_manager label, and story graph downloads. This stays the single producer
 * because interpreting a run needs state the orchestrator has no business holding — who
 * cancelled, and the session latch.
 *
 * FAST / SLOW / ERROR consume the verdict AS IS — which side of the bar the timings
 * landed on was judged inside the run. BUDGET_EXHAUSTED is the exception (TT-1300):
 * the rescue re-checks the already-logged distribution against the tunables in
 * localAsrWarmupTuning.js rather than treating the verdict as a server route. No bar
 * is recomputed; the summary's own `alreadyFastMs` is the bound.
 *
 * `path: 'wordListGrammar'` verdicts (phrase 0's graph never landed) remain admissible
 * for FAST / SLOW — a device slow on the smaller workload is slower on the biased HCLG.
 * The rescue does NOT special-case path either: it can in principle rescue a
 * wordListGrammar run, whose timings describe a lighter workload than real reading. That
 * is left unguarded on purpose — warmup is invoked with the biased graph after phrase
 * 0's .fst lands, so the word-list fallback is effectively never the measured path here;
 * a `path === BIASED_GRAPH` guard would be dead weight. The path rides on the event so
 * fleet analysis can segment them; they must never be pooled for threshold calibration.
 *
 * Applied when the RUN settles, not when the launch stops waiting. On a deadline overrun
 * the story launches routed to the server (TT-1218) and this either confirms that or
 * overrides it to local from the next phrase. Nothing is retroactive.
 * @param {LocalPlacementAsrHandler} handler The handler that owned the run, pinned by the
 *   caller — a run outliving its runtime must not decide for the session that replaced it.
 * @param {number} lifecycleId The runtime generation captured at launch. The handler pin
 *   alone does not close the teardown window: dispose clears the decision synchronously
 *   but nulls `localAsr` only after awaiting handler.dispose(), and that dispose is what
 *   settles a pending run — so a run resolving inside the await would see itself as
 *   current and re-latch a stale decision, which then short-circuits the NEXT run's
 *   already-decided check and sticks for the session. SpeechStream bumps this counter in
 *   dispose before its first await; that ordering is what closes the window, pinned by the
 *   'local ASR warmup wiring (TT-1231)' tests rather than by anything here.
 * @param {object|undefined} summary Resolved summary; undefined when the run failed.
 * @param {object} context
 * @param {string} context.storyId
 * @param {{fired: boolean}} context.deadline Whether the launch deadline cancelled the
 *   run. A box, not a boolean, because it is read at settle time.
 * @param {function(): {handler: object|null, lifecycleId: number}} context.getRuntime
 *   Live read of the runtime identity, so staleness compares against what is current at
 *   settle time rather than at launch.
 * @returns {void}
 */
function applyLocalAsrWarmupRouteDecision(handler, summary, { storyId, deadline, lifecycleId, getRuntime }) {
  // A stale run — its runtime disposed (possibly replaced) while it settled — describes
  // hardware capability that is still true, but its session state is gone: dispose cleared
  // the decision and the latch, and a new handler may already be re-measuring.
  //
  // BOTH checks are load-bearing. The generation one closes the teardown window:
  // `localAsr` is not nulled until after dispose awaits handler.dispose(), and that await
  // is exactly when a pending run settles, so the handler pin still reads as current.
  //
  // The handler check covers a different window: a generation bump BEFORE a write to
  // `localAsr` does not invalidate a pin taken after the bump and before the write, which
  // is the shape of two of SpeechStream's four writers. Deleting it kills no test — not
  // because it is dead weight, but because LocalPlacementAsrHandler currently closes those
  // windows itself (init sets `_ready` only as it returns, and dispose latches `_disposed`
  // synchronously while `_ready` stays true past the drain, so startWarmup declines in
  // both). Those handler invariants are pinned by `readiness invariants relied on by
  // asr/localAsrWarmup (TT-1231)` in LocalPlacementAsrHandler.test.js, so this cannot go
  // stale silently — when either goes red, THIS check is the only guard left.
  //
  // Read through getRuntime, not a captured value: the compare must be against what is
  // current at settle time, ~6.5s later. A parameter would make it vacuous.
  const current = getRuntime();
  if (current.handler !== handler || lifecycleId !== current.lifecycleId) {
    return;
  }
  // This run has settled, so it is no longer in flight past its deadline — release the
  // provisional overrun before deciding anything. Behaviour-neutral today (every path
  // below either latches a decision, which already outranks the flag, or requires the
  // deadline not to have fired), and done unconditionally so that stays true by
  // construction rather than by inspection of the branches. See
  // clearLocalAsrWarmupDeadlineOverrun. Placed AFTER the staleness checks: a stale run
  // must not release an overrun belonging to the runtime that replaced it.
  clearLocalAsrWarmupDeadlineOverrun();
  if (getLocalAsrWarmupRouteDecision()) {
    return;
  }

  const verdict = (summary && summary.verdict) || null;

  if (verdict === WARMUP_VERDICT.CANCELLED && !deadline.fired) {
    // A bare latch release, deliberately NOT resetLocalAsrWarmup(): this path exists so a
    // later story can re-measure, and there is nothing to clear — the early return above
    // means no decision exists, and the overrun was released two lines up.
    localAsrWarmupStarted = false;
    recordLocalAsrLifecycle('placement.warmupRouteUndecided', {
      storyId,
      verdict,
      iterations: summary.iterations,
      latchReleased: true,
    });
    return;
  }

  // No verdict at all: an unexpected-failure run resolves undefined, a dispose
  // mid-run resolves a null verdict. Undecided is the right answer only when
  // nothing measured AND nothing timed out — if the deadline fired, the wait is
  // itself the measurement, exactly as it is for a deadline-cancelled CANCELLED
  // run, and the absence of a verdict must not hand the session the MORE
  // permissive answer than the same device would have got had its run managed to
  // summarise. (A run that failed outright also leaves a runtime no healthier
  // than the one ERROR routes to the server for, so both halves of this case
  // point the same way.) Recorded as a decision rather than an undecided, with a
  // null verdict, because a decision is what it is.
  if (!verdict) {
    if (deadline.fired) {
      setLocalAsrWarmupRouteDecision({ route: LOCAL_ASR_ROUTE.SERVER, verdict: null });
      recordLocalAsrLifecycle('placement.warmupRouteDecided', {
        storyId,
        route: LOCAL_ASR_ROUTE.SERVER,
        verdict: null,
        // Always present on this event so the schema is stable: a fleet query filtering
        // budgetRescue === false must not silently drop this row by matching undefined.
        // A no-verdict deadline run is never a rescue (TT-1300).
        budgetRescue: false,
        deadlineOverrun: true,
        iterations: summary && summary.iterations,
      });
      return;
    }
    recordLocalAsrLifecycle('placement.warmupRouteUndecided', {
      storyId,
      verdict: null,
      latchReleased: false,
    });
    return;
  }

  // FAST routes local outright. BUDGET_EXHAUSTED is the TT-1300 rescue window: a
  // jittery-but-fast device that never latched but whose logged distribution clears
  // the tunables in localAsrWarmupTuning.js routes local, recorded as budgetRescue so
  // the fallback is visible in telemetry. The !deadline.fired guard keeps a
  // deadline-overrun run on server structurally — a budgetExhausted verdict normally
  // settles at the cap, so this excludes the rare run pushed past the cap+1.5s
  // deadline (device slowness, or the post-loop streamFinish draining the decode
  // queue), which fails safe to server. Everything else (SLOW, ERROR, a straddling,
  // heavy-tailed, or thin-sample budgetExhausted) falls through to server.
  let route = LOCAL_ASR_ROUTE.SERVER;
  let budgetRescue = false;
  if (verdict === WARMUP_VERDICT.FAST) {
    route = LOCAL_ASR_ROUTE.LOCAL;
  } else if (
    verdict === WARMUP_VERDICT.BUDGET_EXHAUSTED
    && !deadline.fired
    && budgetExhaustedRoutesLocal(summary)
  ) {
    route = LOCAL_ASR_ROUTE.LOCAL;
    budgetRescue = true;
  }
  setLocalAsrWarmupRouteDecision({ route, verdict });
  // The decision AND its measured basis, together: route answers "where did
  // placement go", the rest answers "on what evidence", and the bar fields make
  // the verdict re-derivable from the row alone. (runtime.warmupEnded carries the
  // full summary too, but that event exists whether or not anything consumes the
  // verdict — this one is the consumption.)
  recordLocalAsrLifecycle('placement.warmupRouteDecided', {
    storyId,
    route,
    verdict,
    // True only when a budgetExhausted run was routed local off its steady-state
    // distribution (TT-1300). On the routing row so a local route reached via the
    // fallback is distinguishable from one a FAST verdict earned.
    budgetRescue,
    deadlineOverrun: deadline.fired,
    realTimeFactor: summary.realTimeFactor,
    audioMsPerIteration: summary.audioMsPerIteration,
    alreadyFastMs: summary.alreadyFastMs,
    iterations: summary.iterations,
    elapsedMs: summary.elapsedMs,
    graphResolveMs: summary.graphResolveMs,
    path: summary.path,
    decodeMode: summary.decodeMode,
    phraseWordCount: summary.phraseWordCount,
    // The distribution behind the verdict, on the ROUTING row and not just on
    // runtime.warmupEnded: realTimeFactor above is one sample, so a straddling device
    // otherwise logs a passing-looking number next to a server route.
    steadyRealTimeFactor: summary.steadyRealTimeFactor,
    // meanInferenceMs stays on the routing row for fleet analysis even though the
    // rescue's tail guard is samplesOverBar / steadySamples, not the mean.
    meanInferenceMs: summary.meanInferenceMs,
    medianInferenceMs: summary.medianInferenceMs,
    p75InferenceMs: summary.p75InferenceMs,
    maxInferenceMs: summary.maxInferenceMs,
    samplesOverBar: summary.samplesOverBar,
    steadySamples: summary.steadySamples,
  });
}

/**
 * Measures whether this device can decode fast enough to serve reading locally, once
 * per session, behind the pre-story loader.
 *
 * Called by ReaderManager after the per-story .fst download resolves and before the
 * activity launches. Both halves matter: AFTER the download because the point is to time
 * phrase 0's biased HCLG, and until the bytes land there is no graph to time; BEFORE the
 * launch because the loader is already on screen, so the CPU is dead time the student waits
 * through rather than competing with live decode for the one core.
 *
 * Awaited, so in the normal case the verdict exists — and, since TT-1169, the
 * session's placement routing decision derived from it is latched — before the
 * first phrase. A fast device short-circuits in three stride-sized iterations — a
 * few hundred milliseconds — and a slow one is stopped by the handler's cap. The
 * one case that reaches the first phrase WITHOUT a verdict is the deadline below,
 * which is the deliberate trade: no verdict beats a story that will not start.
 * Even then the run settles and decides late, applying from the next phrase — see
 * applyLocalAsrWarmupRouteDecision for what every outcome means for routing. And
 * no verdict is not no answer: once the deadline has fired, the wait itself is
 * evidence of a device that could not finish one iteration inside cap + 1.5s, so
 * phrase 0 routes to the server on it provisionally rather than arming locally
 * against a run that is still unwinding (TT-1218; the orchestrator's
 * isLocalAsrProvisionallyServerRouted is what every consumer asks).
 *
 * Never throws, for the same reason. Every failure path here resolves.
 *
 * A call that does not result in a run — no storyId, a story whose phrases have not
 * hydrated, a runtime not yet ready, or a handler that declines — is a no-op that
 * does NOT consume the once-per-session latch, and records why. So the callers that
 * fire speculatively, and the half-hydrated story objects they fire with, cannot rob
 * the one real story of its measurement.
 * @param {object} story The fetched story; storyId identifies phrase 0's graph and,
 *   with a non-empty phrase list, gates the run — without both there is nothing to
 *   read aloud and so nothing worth measuring.
 * @param {object} options
 * @param {function(): {handler: object|null, lifecycleId: number}} options.getRuntime
 *   Live read of the local ASR runtime's identity. An accessor rather than plain
 *   values because the staleness guards downstream must compare this run's pin
 *   against what is current when the run settles, ~6.5s later. Must not throw:
 *   the initial call below sits outside this function's try block, so it is not
 *   covered by the never-throws guarantee above — that guarantee is a precondition
 *   on this accessor, not something this function enforces on its behalf.
 * @returns {Promise<void>}
 */
export async function warmLocalAsrForStory(story, { getRuntime }) {
  const storyId = story && story.storyId;
  // NOTHING TO READ ALOUD, NO WARMUP — and, just as importantly, no latch.
  // ReaderManager's mount/didUpdate catch-alls fire with whatever is in props, and
  // at the story picker that is nothing at all. Warming there did two bad things at
  // once: it measured the word-list grammar, which is the exact workload TT-1209
  // exists to stop measuring, and it spent the once-per-session latch — so the real
  // story-load call arrived to find warmup already "done" and the biased-graph
  // measurement never ran for the rest of the session.
  //
  // The phrase-count half catches the same robbery one level deeper: a story can
  // arrive mid-hydration with a storyId but no phrases yet. Its graphs are keyed by
  // phrase, so there are no bytes to warm against and the run would fall back to the
  // word list — and the populated story, moments away, would find the latch already
  // spent. The downloader declines the identical shape for the identical reason
  // (storyLmGraphDownloader: `!storyId || phraseCount === 0`), so gating on the same
  // helper keeps the two in agreement about what a warmable story is.
  //
  // Returning before the latch is what makes the later call the one that counts.
  if (!storyId || getStoryPhraseCount(story) === 0) {
    return;
  }
  // ONE read for the gates AND the pin. Everything from here to startWarmup below is
  // synchronous, so these cannot drift apart — and taking them together makes explicit
  // that the runtime the gates check is the runtime this run belongs to.
  //
  // Pinned for the whole call, including the deadline callback below. Everything
  // after this point must act on the handler this run belongs to, not on whatever
  // SpeechStream's `localAsr` happens to hold when the timer fires: dispose clears the
  // latch, so within the ~6.5s wait a teardown and re-init can install a NEW
  // handler and a new story can start warming it. Reading the global there would
  // cancel that run and report its iteration count under this story's id — one
  // inexplicable S3 row, impossible to reconstruct after the fact.
  //
  // The runtime GENERATION this run belongs to. Both dispose and init bump this
  // before their first await, so it invalidates a stale run in the teardown
  // window the handler pin cannot see — see applyLocalAsrWarmupRouteDecision.
  const { handler, lifecycleId } = getRuntime();
  if (localAsrWarmupStarted || !handler) {
    return;
  }
  // Init assigns localAsr BEFORE awaiting handler.init(), so "runtime exists but is
  // not ready yet" is a real race a story load can land in — and it is the one skip
  // worth a record. The others are all expected noise: no story fires on every story
  // picker render, the latch is the normal answer for stories 2..N, and no runtime at
  // all is just a session local ASR was never part of. Silence on those keeps the
  // timeline readable; silence on THIS one made a session that lost its measurement
  // to a race look identical in S3 to one where warmup was never called.
  // recordLocalAsrLifecycle, not recordLocalAsrFailure: an expected race, not a fault.
  if (!handler.isReady) {
    recordLocalAsrLifecycle('runtime.warmupSkipped', {
      reason: LOCAL_ASR_FALLBACK_REASON.RUNTIME_NOT_READY,
      storyId,
    });
    return;
  }
  // Via the shared reader, not story.phrases directly: selectStory — the mainline,
  // student-picked path — hands over the RAW getStory shape, which nests phrases
  // under chapters[0] and has no story.phrases at all. Reading the flat field alone
  // reported phraseWordCount for the props-driven paths and null for every story a
  // student actually chose, which is the majority of the calibration data.
  const phrases = getStoryPhrases(story);
  const firstPhrase = Array.isArray(phrases) ? phrases[0] : null;
  let deadlineTimer;
  try {
    const run = handler.startWarmup({
      // Phrase 0 specifically: it is the graph that has landed by now, and the one
      // the first decodes will use.
      biasedGraph: { storyId, phraseIndex: 0 },
      // HCLG decode cost scales with the graph, and graph size tracks phrase length,
      // so a short opening phrase decodes faster than a long mid-story one. The
      // measured baseline is therefore ONE SAMPLE of a per-story distribution, not
      // the distribution — TT-1179's calibration needs this to know how much of the
      // margin is being spent absorbing that variance.
      phraseWordCount: typeof firstPhrase === 'string'
        ? firstPhrase.trim().split(/\s+/).filter(Boolean).length
        : null,
    });
    // LATCH ON THE OUTCOME, NOT ON HAVING ASKED. startWarmup declines for reasons
    // this function cannot see and should not have to mirror — a capture already
    // live, a disposed handler — and spending the latch on a decline costs the
    // session its only measurement with nothing recorded to say why. Three bugs on
    // this path have now been exactly that shape, each because a new decline
    // condition appeared the caller did not know about. `isWarmingUp` is set
    // synchronously on accept, so it answers the only question that matters here and
    // keeps answering it as those conditions change.
    //
    // Still set before the first await, so the race the latch exists for — two
    // stories selected in quick succession, a didUpdate catch-all against an explicit
    // selectStory — stays closed. And still not deferred to completion, which would
    // re-run a full warmup for every story whose graphs are legitimately unpublished.
    if (!handler.isWarmingUp) {
      // The handler resolves rather than rejects on a decline, but this path returns
      // without awaiting `run`, so anything that ever did reject would surface as an
      // unhandled rejection rather than as the warmupThrew the catch below files.
      Promise.resolve(run).catch(() => {});
      recordLocalAsrLifecycle('runtime.warmupSkipped', {
        reason: LOCAL_ASR_FALLBACK_REASON.HANDLER_DECLINED,
        storyId,
      });
      return;
    }
    localAsrWarmupStarted = true;
    // A box rather than a boolean because the routing decision reads it when the
    // RUN settles, which on a deadline overrun is after the timer below has fired —
    // a boolean captured when the continuation was attached would still be false.
    const deadline = { fired: false };
    // The box stays per-run and local: the decision continuation must answer for the
    // deadline IT raced, not for whichever run is current ~6.5s later, which is the same
    // reason it takes a pinned handler and lifecycleId. The session-level "is the current
    // run overrun" answer is pushed to the orchestrator from the timer below instead.
    // The decision is attached to the run itself, not to the race: a run that
    // outlives the deadline still settles, and its verdict still decides the
    // session's routing — it just applies from the next phrase instead of the
    // first. This is what gives a late verdict its defined meaning.
    const decided = Promise.resolve(run).then(
      (summary) => applyLocalAsrWarmupRouteDecision(handler, summary, {
        storyId,
        deadline,
        lifecycleId,
        getRuntime,
      }),
    );
    // Bound the WAIT, not the run. The handler's own cap stops it starting new
    // iterations, but it will not abandon one already in flight — cutting a decode
    // off mid-flight neither reclaims the CPU nor yields a usable timing. A WEDGED
    // decode is the gap that leaves: the engine only gives up on it at its 8s decode
    // timeout, which would hold the loader well past the cap on the devices least
    // able to afford the wait. So the story stops waiting here, cancels the run so
    // it stops after its in-flight iteration, and launches. The run still settles
    // and still logs its summary — it just does so with nobody waiting.
    // Races `decided`, not `run`, so on the mainline path the routing decision is
    // latched — not merely latchable — before the loader comes down and the first
    // phrase can arm.
    await Promise.race([
      decided,
      new Promise((resolve) => { deadlineTimer = setTimeout(resolve, LOCAL_ASR_WARMUP_DEADLINE_MS); })
        .then(() => {
          // Whether this is a fault depends on whether the run was measuring. The cap
          // is checked BEFORE each iteration, so a run can start its last one just
          // under the cap and finish past this deadline — on hardware slower than
          // ~1500ms per stride that is the NORMAL ending of a healthy-but-slow run,
          // not a stall, and filing it as a failure would put a fault entry on exactly
          // the devices TT-1179 needs to characterise. Zero iterations is the case
          // that really is stuck: the runtime never produced a single decode.
          const iterations = handler.warmupIterations || 0;
          const detail = { storyId, iterations };
          if (iterations > 0) {
            recordLocalAsrLifecycle('runtime.warmupDeadline', detail);
          } else {
            recordLocalAsrFailure('runtime.warmupDeadline', detail);
          }
          // Marked BEFORE the cancel, so by the time the flag can end the run the
          // decision continuation is guaranteed to see who cancelled it — a
          // deadline cancel is an implicit slowness measurement and routes server,
          // where a live-audio cancel decides nothing.
          deadline.fired = true;
          // Published to the orchestrator so every consumer of the routing question
          // gets the provisional answer, not just SpeechStream's per-phrase gate (TT-1218).
          // Generation-guarded for the same reason the decision continuation is: a
          // teardown inside this ~6.5s wait does not stop the timer, so an unguarded
          // set here would leave a disposed run's overrun gating the session that
          // replaced it — with no verdict of its own to clear it.
          if (lifecycleId === getRuntime().lifecycleId) {
            setLocalAsrWarmupDeadlineOverrun();
          }
          // Pinning is what makes this safe across a teardown: it used to read the
          // module global, which dispose nulls, so a teardown inside the wait threw a
          // TypeError the catch below filed as runtime.warmupThrew — a fault entry for
          // an orderly shutdown. Cancelling an already-disposed handler just sets a
          // flag nobody reads. The optional chain is belt-and-braces in a callback
          // that must not throw; `handler` cannot actually be null here.
          handler?.cancelWarmup();
        }),
    ]);
  } catch (error) {
    recordLocalAsrFailure('runtime.warmupThrew', { storyId, error: error?.message });
  } finally {
    clearTimeout(deadlineTimer);
  }
}

/**
 * Releases the session's warmup state for a runtime that is being torn down.
 *
 * The latch and the orchestrator's routing state reset TOGETHER OR NOT AT ALL: a kept
 * decision with a released latch would let a stale verdict gate a runtime it never
 * measured, and the reverse would re-measure without ever consuming the result. Worse
 * since TT-1218, a stale deadline overrun would route every phrase of the NEXT session
 * to the server on the strength of a run that measured the runtime being disposed.
 *
 * They are one function rather than two adjacent calls so that pairing is structural —
 * there is no longer a way to reset one and forget the other.
 *
 * Called only by disposeLocalPlacementAsr. For why init does not also call it despite
 * bumping the same generation counter, see the invariant note on
 * resetLocalAsrWarmupRouting in CursorOrchestrator.
 * @returns {void}
 */
export function resetLocalAsrWarmup() {
  localAsrWarmupStarted = false;
  resetLocalAsrWarmupRouting();
}
